/**
 * AdminPayoutQueue.jsx — Phase 3 admin queue for writer payout requests.
 *
 * Filter by status (active/submitted/pending/approved/paid/rejected/all),
 * one-click advance a request through the SUBMITTED → PENDING → APPROVED →
 * PAID pipeline, or reject with a reason. Coin balance side-effects are
 * server-side — we just trigger transitions and reload.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2, XCircle, Clock, Hourglass, Banknote, Loader2, RefreshCw, User,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  submitted: { label: 'Submitted', colour: '#94A3B8', icon: Clock },
  pending:   { label: 'Pending',   colour: '#F59E0B', icon: Hourglass },
  approved:  { label: 'Approved',  colour: '#3B82F6', icon: CheckCircle2 },
  paid:      { label: 'Paid',      colour: '#10B981', icon: Banknote },
  rejected:  { label: 'Rejected',  colour: '#EF4444', icon: XCircle },
};

const NEXT_ACTION = {
  submitted: { next: 'pending',  label: 'Move to pending',  tone: 'bg-amber-500 hover:bg-amber-600 text-slate-900' },
  pending:   { next: 'approved', label: 'Approve',          tone: 'bg-blue-500 hover:bg-blue-600 text-white' },
  approved:  { next: 'paid',     label: 'Mark as paid',     tone: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
};

const AdminPayoutQueue = () => {
  const [filter, setFilter] = useState('active');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (status = filter) => {
    setLoading(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/admin/payouts?status=${status}`);
      const d = await res.json();
      setRows(d.requests || []);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);

  const advance = async (row) => {
    const action = NEXT_ACTION[row.status];
    if (!action) return;
    setBusyId(row.requestId);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/admin/payouts/${row.requestId}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: action.next }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || `HTTP ${res.status}`);
      toast.success(`${row.requestId}: ${action.label}`);
      load();
    } catch (e) {
      toast.error(e.message || 'Transition failed');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (row) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason || !reason.trim()) return;
    setBusyId(row.requestId);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/admin/payouts/${row.requestId}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected', reason: reason.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || `HTTP ${res.status}`);
      toast.success(`${row.requestId} rejected — coins released back to writer.`);
      load();
    } catch (e) {
      toast.error(e.message || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="admin-payout-queue">
      <CardHeader className="flex-row items-center justify-between flex-wrap gap-3 space-y-0">
        <CardTitle className="text-white flex items-center gap-2">
          <Banknote className="w-5 h-5 text-emerald-400" /> Payout Queue
        </CardTitle>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="admin-payout-filter">
          {['active', 'submitted', 'pending', 'approved', 'paid', 'rejected', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-payout-filter-${s}`}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors capitalize ${
                filter === s
                  ? 'bg-emerald-600 border-emerald-500 text-white'
                  : 'bg-slate-800 border-slate-600 text-gray-300 hover:border-slate-500'
              }`}
            >
              {s}
            </button>
          ))}
          <button
            type="button"
            onClick={() => load()}
            title="Refresh"
            className="text-gray-400 hover:text-white p-1"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-400 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading queue…
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="admin-payout-empty">
            No payout requests match this filter.
          </p>
        ) : (
          <ul className="space-y-3">
            {rows.map((r) => {
              const m = STATUS_META[r.status] || STATUS_META.submitted;
              const Icon = m.icon;
              const action = NEXT_ACTION[r.status];
              return (
                <li
                  key={r.requestId}
                  className="p-4 bg-slate-900/40 rounded-lg border border-slate-700 flex flex-col lg:flex-row lg:items-center gap-4"
                  data-testid={`admin-payout-row-${r.requestId}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border"
                        style={{
                          backgroundColor: `${m.colour}22`,
                          color: m.colour,
                          borderColor: `${m.colour}55`,
                        }}
                      >
                        <Icon className="w-3 h-3" />
                        {m.label}
                      </span>
                      <span className="text-[11px] text-gray-400">
                        {new Date(r.requestDate).toLocaleString()}
                      </span>
                      <span className="text-[11px] text-gray-500 font-mono">{r.requestId}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-white font-semibold">{r.fullLegalName || '—'}</span>
                      <span className="text-gray-400">· {r.coinsWithdrawn?.toLocaleString()} coins</span>
                      <span className="text-emerald-300 font-bold">£{Number(r.gbpEquivalent || 0).toFixed(2)}</span>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {r.method === 'bank'
                        ? `Bank · ${r.details?.bankName || '—'} · ${r.details?.accountNumberMasked || '••••'} · Sort ${r.details?.sortCode || '—'}`
                        : `PayPal · ${r.details?.paypalEmailMasked || '•••'}`}
                    </div>
                    {r.rejectionReason && (
                      <p className="text-[11px] text-rose-300 mt-1">Reason: {r.rejectionReason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {action && (
                      <Button
                        onClick={() => advance(r)}
                        disabled={busyId === r.requestId}
                        className={`${action.tone} text-xs font-semibold`}
                        data-testid={`admin-payout-advance-${r.requestId}`}
                      >
                        {busyId === r.requestId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : action.label}
                      </Button>
                    )}
                    {['submitted', 'pending', 'approved'].includes(r.status) && (
                      <Button
                        onClick={() => reject(r)}
                        disabled={busyId === r.requestId}
                        variant="outline"
                        className="text-xs text-rose-300 border-rose-500/40 hover:bg-rose-500/10"
                        data-testid={`admin-payout-reject-${r.requestId}`}
                      >
                        Reject
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPayoutQueue;
